import { ReactNode } from 'react';
import { attributesToProps } from 'html-react-parser';
import styled from 'styled-components';
import Button from '@discover-ui/button';
import { openConsentPrompt, useConsent, useConsentPromptOpen } from '@/lib/consent';
import { getEmbedProviderLabel, isTrackingEmbed, toPrivacyEnhancedSrc } from './trackingEmbeds';

const IframeWrapper = styled.span`
  display: block;
  position: relative;
  overflow: hidden;
  width: 100%;
  margin-bottom: 30px;
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
  }
`;

type ConsentGatedIframeProps = {
  attribs: Record<string, string>;
  children?: ReactNode;
};

const getAspectRatio = (attribs: Record<string, string>): number => {
  const width = Number(attribs.width);
  const height = Number(attribs.height);

  if (width > 0 && height > 0) {
    return (height / width) * 100;
  }

  return 100;
};

/**
 * Renders blacklisted third-party CMS embeds only once consent allows it, showing a "Manage cookies" placeholder until then. Withholding the iframe is the only lever — the cookies it sets are third-party, so they can't be cleared on withdrawal. Non-blacklisted and non-GDPR iframes render unchanged.
 */
const ConsentGatedIframe = ({ attribs, children }: ConsentGatedIframeProps) => {
  const { allowed, isGdprRegion, hydrated, isUndecided } = useConsent();
  const promptOpen = useConsentPromptOpen();

  const aspectRatio = getAspectRatio(attribs);
  const src = attribs.src ?? '';

  // `overrideSrc` is only passed for the post-consent YouTube rewrite; otherwise the iframe renders exactly as authored (including no `src` attribute when the CMS omitted one).
  const renderIframe = (overrideSrc?: string) => (
    <IframeWrapper className="bg-pulse-animation" style={{ paddingTop: `${aspectRatio}%` }}>
      {/* Default title for a11y; a CMS-authored `title` in attribs overrides it via the spread. */}
      <iframe
        title="Embedded content"
        {...attributesToProps(attribs)}
        {...(overrideSrc && { src: overrideSrc })}>
        {children}
      </iframe>
    </IframeWrapper>
  );

  // Non-blacklisted embeds and all non-GDPR traffic render exactly as authored. isGdprRegion is checked first so non-GDPR traffic skips the URL parse in isTrackingEmbed.
  if (!isGdprRegion || !isTrackingEmbed(src)) {
    return renderIframe();
  }

  // GDPR + tracking embed below. Until hydration the persisted decision is unknown: reserve the embed's space with an empty box (no placeholder flash for returning accepters) so swapping in the iframe or placeholder doesn't shift layout. Matches the undecided server snapshot, so no hydration mismatch.
  if (!hydrated) {
    return (
      <span
        className="mb-[30px] block w-full"
        style={{ paddingTop: `${aspectRatio}%` }}
        aria-hidden="true"
      />
    );
  }

  if (allowed) {
    return renderIframe(toPrivacyEnhancedSrc(src));
  }

  const providerLabel = getEmbedProviderLabel(src);
  // The global consent banner is already on screen while the diner is undecided or has re-fired it, so only offer the button otherwise — a second trigger would no-op.
  const bannerOpen = isUndecided || promptOpen;

  return (
    <span
      className="mb-[30px] block w-full rounded-md border border-ds-gray-light-50 bg-ds-gray-light-25"
      style={{ paddingTop: `${aspectRatio}%`, position: 'relative' }}>
      <span className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center">
        {/* span (block) rather than <p>: the embed can sit inside a CMS <p>, where block flow
            content would be invalid; the outer wrapper is a span for the same reason. */}
        <span className="block text-sm leading-normal text-ds-gray-light-700">
          {providerLabel ? `This ${providerLabel} content` : 'This embedded content'} needs cookies
          to load. Accept cookies to view it.
        </span>
        {!bannerOpen && (
          <Button type="button" kind="normal" scale="md" onClick={() => openConsentPrompt()}>
            Manage cookies
          </Button>
        )}
      </span>
    </span>
  );
};

export default ConsentGatedIframe;
